import { EventBus, EVENTS, type EventName } from '../../../shared/EventBus';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import type { CastTimelineSystem } from './CastTimelineSystem';

export const WINDUP_OWNER_CAP = 128;
const scopes = new WeakMap<object, number>();
const activeOwners = new Set<SequencedCastWindup>();
let nextScope = 0, nextOwner = 0, nextLegacy = 0;
const sequence = (value: unknown): value is number => Number.isSafeInteger(value) && (value as number) > 0;

/** The one scene VFX owner also retires local tells on a full renderer teardown. */
export function clearActiveCastWindups(): void {
  for (const owner of [...activeOwners]) owner.clear();
}

/** A scene/room namespace is client-only and never becomes network authority. */
export function castWindupScope(): string {
  const registry = ServiceRegistry.getInstance();
  const key = (owner: object | undefined | null) => {
    if (!owner) return 0;
    let id = scopes.get(owner);
    if (!id) { id = ++nextScope; scopes.set(owner, id); }
    return id;
  };
  return `${key(registry.get('NetworkManager', { optional: true })?.getRoom?.())}:${key(registry.get('EntitySystem', { optional: true }))}`;
}

export interface CastWindupOptions {
  casterId: string;
  abilityId: string;
  clientCastSeq?: number;
  legacyIdentity?: object;
  duration: number;
  /** Schema identity/life plus current render actor; evaluated again at release. */
  isCurrent(): boolean;
  tell(instanceId: string): void;
  complete(): void;
  /** A predicted replacement is not proof the server accepted it. */
  ignorePredictedOverwrite?: boolean;
  onRetire?: () => void;
}
interface Windup extends CastWindupOptions {
  instanceId: string;
  scope: string;
  timer: ReturnType<typeof setTimeout>;
  timeline?: CastTimelineSystem;
}
interface Dependencies {
  scope?: () => string;
  timeline?: () => CastTimelineSystem | undefined;
  stop?: (instanceId: string) => void;
}

/** Owns one pending tell/release per caster. Timers, not cast-bar replacement,
 * own release: a server-refused predicted replacement cannot erase a Howl.
 * Only Howl opts into this helper; existing wind-up policies stay unchanged. */
export class SequencedCastWindup {
  private readonly ownerId = ++nextOwner;
  private readonly rows = new Map<string, Windup>();
  private readonly highWater = new Map<string, number>();
  private readonly legacySeen = new WeakSet<object>();
  private subscriptions: Array<() => void> = [];
  private generation = 0;
  private epoch = 0;
  private clearing = false;
  private scope: string | null = null;

  constructor(private readonly deps: Dependencies = {}) {}

  start(options: CastWindupOptions): boolean {
    if (this.clearing) return false;
    const scope = this.currentScope();
    if (this.scope !== scope) { this.clear(); this.scope = scope; }
    if (!options.casterId || !Number.isFinite(options.duration) || options.duration <= 0
      || options.duration > 60_000 || !options.isCurrent()) return false;
    if (options.clientCastSeq !== undefined && !sequence(options.clientCastSeq)) return false;
    if (sequence(options.clientCastSeq)) {
      if (options.clientCastSeq <= (this.highWater.get(options.casterId) ?? 0)) return false;
      this.highWater.delete(options.casterId);
      this.highWater.set(options.casterId, options.clientCastSeq);
      while (this.highWater.size > WINDUP_OWNER_CAP) this.highWater.delete(this.highWater.keys().next().value!);
    } else if (options.legacyIdentity) {
      if (this.legacySeen.has(options.legacyIdentity)) return false;
      this.legacySeen.add(options.legacyIdentity);
    }
    const epoch = this.epoch;
    const previous = this.rows.get(options.casterId);
    if (previous) this.retire(previous);
    if (this.rows.size >= WINDUP_OWNER_CAP) this.retire(this.rows.values().next().value!);
    if (epoch !== this.epoch || scope !== this.currentScope() || this.rows.has(options.casterId)
      || this.rows.size >= WINDUP_OWNER_CAP || !options.isCurrent()
      || (sequence(options.clientCastSeq) && this.highWater.get(options.casterId) !== options.clientCastSeq)) return false;
    const timeline = this.deps.timeline?.() ?? ServiceRegistry.getInstance().get('CastTimelineSystem', { optional: true });
    const row: Windup = { ...options, scope, timeline,
      instanceId: `cast-windup:${this.ownerId}:${options.casterId}:${options.abilityId}:${options.clientCastSeq ?? `legacy-${++nextLegacy}`}`,
      timer: setTimeout(() => this.finish(row), options.duration) };
    this.rows.set(options.casterId, row);
    this.bind();
    timeline?.startCast({ casterId: row.casterId, abilityId: row.abilityId, clientCastSeq: row.clientCastSeq,
      startTime: Date.now(), totalDuration: row.duration, events: [],
      interruptible: false, movementCancels: false });
    if (this.rows.get(row.casterId) === row && this.valid(row)) row.tell(row.instanceId);
    else if (this.rows.get(row.casterId) === row) this.retire(row);
    return this.rows.get(row.casterId) === row;
  }

  /** Only the same sequence (or legacy-to-legacy) can retire a pending cast. */
  cancel(casterId: string, abilityId?: string, clientCastSeq?: number): void {
    const row = this.rows.get(casterId);
    if (!row || (abilityId !== undefined && row.abilityId !== abilityId)) return;
    if (abilityId !== undefined && row.clientCastSeq !== clientCastSeq) return;
    this.retire(row);
  }

  clear(): void {
    ++this.epoch;
    this.clearing = true;
    try {
      for (const row of [...this.rows.values()]) this.retire(row);
      this.highWater.clear();
      this.unbind();
    } finally { this.clearing = false; }
  }

  activeCount(): number { return this.rows.size; }

  private currentScope(): string { return this.deps.scope?.() ?? castWindupScope(); }
  private valid(row: Windup): boolean { return row.scope === this.currentScope() && row.isCurrent(); }

  private finish(row: Windup): void {
    if (this.rows.get(row.casterId) !== row) return;
    const epoch = this.epoch;
    const current = this.valid(row);
    this.retire(row);
    if (current && epoch === this.epoch && !this.rows.has(row.casterId)
      && row.scope === this.currentScope() && row.isCurrent()) row.complete();
  }

  private retire(row: Windup): void {
    if (this.rows.get(row.casterId) !== row) return;
    this.rows.delete(row.casterId);
    clearTimeout(row.timer);
    this.retireTimeline(row);
    if (this.rows.size === 0) this.unbind();
    (this.deps.stop ?? (instanceId => EventBus.getInstance().emit(EVENTS.VFX_STOP_INSTANCE, { instanceId })))(row.instanceId);
    row.onRetire?.();
  }

  private retireTimeline(row: Windup): void {
    row.timeline?.retireCast(row.casterId, row.abilityId, row.clientCastSeq);
  }

  private bind(): void {
    if (this.subscriptions.length) return;
    activeOwners.add(this);
    const bus = EventBus.getInstance(), generation = ++this.generation;
    const sub = (event: EventName, callback: (data: any) => void) => {
      this.subscriptions.push(bus.on(event, (data?: unknown) => { if (generation === this.generation) callback(data); }));
    };
    sub(EVENTS.COMBAT_CAST_CANCELLED, data => {
      const row = this.rows.get(data?.casterId);
      if (!row || (data.reason === 'overwrite' && row.ignorePredictedOverwrite)) return;
      this.cancel(data.casterId, data.abilityId, data.clientCastSeq);
    });
    sub(EVENTS.NET_MESSAGE_ABILITY_CAST_CANCELLED, data => {
      if (data?.ability_id !== 'howl_of_terror' || !['interrupt', 'overwrite', 'leave'].includes(data.reason)) return;
      this.cancel(data.playerId, data.ability_id, data.client_cast_seq);
    });
    sub(EVENTS.ABILITY_CASTED, data => {
      const row = this.rows.get(data?.playerId);
      if (!row || (data.ability_id === row.abilityId && data.client_cast_seq === row.clientCastSeq)) return;
      if (sequence(row.clientCastSeq) && (!sequence(data.client_cast_seq) || data.client_cast_seq <= row.clientCastSeq)) return;
      this.retire(row); // This event is an accepted server cast, unlike a local timeline overwrite.
    });
    for (const event of [EVENTS.NET_DISCONNECTED, EVENTS.LEVEL_UNLOADED, EVENTS.MAP_SERVER_SWITCH_START]) sub(event, () => this.clear());
    sub('net:remotePlayerLeave', data => { if (data?.sessionId) this.cancel(data.sessionId); });
    sub(EVENTS.WIRE_PLAYER_DIED, data => { if (data?.victimSid) this.cancel(data.victimSid); });
    sub(EVENTS.WIRE_PLAYER_RESPAWNED, data => { if (data?.victimSid) this.cancel(data.victimSid); });
  }

  private unbind(): void {
    activeOwners.delete(this);
    ++this.generation;
    for (const unsubscribe of this.subscriptions) unsubscribe();
    this.subscriptions = [];
  }
}
